import axios from "axios";

// Keep the API client independent from App.js. App.js renders GentleflowChat,
// which imports this module, so importing BACKEND_URL from App.js creates a
// circular dependency and can trigger a Temporal Dead Zone crash in production
// builds. The API base URL is a static deployment configuration and belongs here
// (or in a dependency-free config module), not in the React component graph.
const BACKEND_URL = "https://gentleflow-production.up.railway.app";

export const GENTLEFLOW_API = `${BACKEND_URL}/api`;

const getToken = () => {
  try {
    return localStorage.getItem("gentleflow_token");
  } catch {
    return null;
  }
};

const client = axios.create({
  baseURL: GENTLEFLOW_API,
  withCredentials: false,
  timeout: 30000,
  headers: { Accept: "application/json" }
});

client.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

client.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config || {};
    const method = String(config.method || "get").toLowerCase();
    const status = error?.response?.status;
    const transient =
      !error?.response ||
      status === 408 ||
      status === 425 ||
      status === 429 ||
      (status >= 500 && status <= 599);

    if ((method === "get" || method === "head") && transient && !config.__gentleflowRetried) {
      config.__gentleflowRetried = true;
      await new Promise((resolve) => setTimeout(resolve, 450));
      return client(config);
    }
    return Promise.reject(error);
  }
);

export const apiGet = (path, config = {}) => client.get(path, config);
export const apiPost = (path, data, config = {}) => client.post(path, data, config);
export const apiPut = (path, data, config = {}) => client.put(path, data, config);
export const apiDelete = (path, config = {}) => client.delete(path, config);

export const getApiErrorMessage = (error, fallback = "The service is temporarily unavailable.") => {
  if (error?.response?.data?.detail) return String(error.response.data.detail);
  if (error?.response?.status === 401) return "Your session has expired. Please sign in again.";
  if (error?.response?.status === 429) return "You're sending requests a little too quickly. Please wait a moment.";
  if (error?.code === "ECONNABORTED") return "The request timed out. Please try again.";
  if (!error?.response) return "We couldn't reach Gentleflow right now. Please check your connection and try again.";
  return fallback;
};

export default client;
